import { useEffect, useMemo, useState, Suspense, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Bell, Menu, Search, Settings, UserRound } from 'lucide-react'
import { useNavigationTracker } from '../hooks/useGoBack'
import { PermissionAwareSidebar } from '../components/PermissionAwareSidebar'
import { Header, getTeacherBreadcrumbParts, getAdminBreadcrumbParts } from '../components/Header'
import { Avatar } from '../components/Avatar'
import { Typography } from '../components/Typography'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { useFcmPushNotification } from '../features/notifications/hooks/useFcmPushNotification'
import { PageSkeleton } from '../components/Skeleton'

const teacherPageTitles: Record<string, string> = {
  '/teacher/dashboard': 'Dashboard',
  '/teacher/myclasses': 'My Classes',
  '/teacher/attendence': 'Attendance',
  '/teacher/assignments': 'Assignments',
  '/teacher/assignments/create': 'Create Assignment',
  '/teacher/assignments/review': 'Review Submissions',
  '/teacher/homework': 'Homework',
  '/teacher/homework/create': 'Create New Homework',
  '/homework': 'Homework',
  '/homework/create': 'Create New Homework',
  '/teacher/examinations': 'Examination and Marks',
  '/teacher/lessonplans': 'Lesson Plans',
  '/admin/lessonplans': 'Lesson Plans',
  '/teacher/lessonplans/create': 'Create Lesson Plan',
  '/teacher/library': 'Library',
  '/teacher/announcements': 'Communication',
  '/teacher/communication': 'Communication',
  '/teacher/student-performance': 'Student Performance',
  '/teacher/leave-management': 'Apply leave',
  '/teacher/leave-management/history': 'Apply leave',
  '/teacher/leave-management/calendar': 'Apply leave',
  '/teacher/leave-management/history/details': 'Apply leave',
}

type AdminSearchItem = {
  title: string
  section: string
  path: string
  keywords: string
}



const adminSearchItems: AdminSearchItem[] = [
  {
    title: 'Dashboard',
    section: 'Main',
    path: '/admin/dashboard',
    keywords: 'school admin dashboard overview admissions approvals shortcuts analytics summary',
  },
  {
    title: 'User Management',
    section: 'User & Role Management',
    path: '/admin/user-management',
    keywords: 'user management role management users roles access permissions staff accounts login',
  },
  {
    title: 'Department Management',
    section: 'User & Role Management',
    path: '/admin/user-management/departments',
    keywords: 'department management departments organization active inactive employees academic finance hr admin',
  },
  {
    title: 'Student Directory',
    section: 'Front Office',
    path: '/admin/student-directory',
    keywords: 'student directory student management students active inactive new admissions front office',
  },
  {
    title: 'Leave Management',
    section: 'Leave Management',
    path: '/admin/leave-management',
    keywords: 'leave management leave requests approvals teacher leave staff leave HR',
  },
  {
    title: 'Admissions',
    section: 'Admission Management',
    path: '/admin/admission',
    keywords: 'admissions enquiries inquiry leads prospects admission management',
  },
  {
    title: 'New Application',
    section: 'Admission Management',
    path: '/admin/application/new',
    keywords: 'new application add student enquiry admission intake',
  },
  {
    title: 'Admissions Reports',
    section: 'Admission Management',
    path: '/admin/admission/reports',
    keywords: 'admissions reports export csv pdf summary',
  },
  {
    title: 'Applications',
    section: 'Admission Management',
    path: '/admin/admission',
    keywords: 'applications application forms candidates submitted admission application',
  },
  {
    title: 'Interview',
    section: 'Admission Management',
    path: '/admin/interview',
    keywords: 'interview schedule evaluation candidate meetings',
  },
  {
    title: 'Document Verification',
    section: 'Front Office',
    path: '/admin/document-verification',
    keywords: 'document verification admissions checks missing documents approve verify validate',
  },
  {
    title: 'Class & Section Setup',
    section: 'Front Office',
    path: '/admin/enrollment/class-section-setup',
    keywords: 'seat management admission capacity grade sections vacancies criteria classroom allocation',
  },
  {
    title: 'Add Class',
    section: 'Admission Management',
    path: '/admin/enrollment/class-section-setup/add-class',
    keywords: 'add grade seat management create grade section capacity vacancies',
  },
  {
    title: 'Add Section',
    section: 'Admission Management',
    path: '/admin/enrollment/class-section-setup/add-section',
    keywords: 'add section seat management create section capacity vacancies',
  },
  {
    title: 'Set Criteria',
    section: 'Admission Management',
    path: '/admin/enrollment/class-section-setup/set-criteria',
    keywords: 'set criteria seat management thresholds eligibility average cutoff admission',
  },
  {
    title: 'Track Candidates',
    section: 'Admission Management',
    path: '/admin/track-candidates',
    keywords: 'track candidates remarks selection hold rejected scheduled interview',
  },
  {
    title: 'Fees & Finance',
    section: 'Finance Management',
    path: '/admin/finance/fees-finance',
    keywords: 'fees finance overview collection reports dashboard money rupees revenue',
  },
  {
    title: 'Examination Management',
    section: 'Examination Management',
    path: '/admin/academic/examinations',
    keywords: 'examinations exam dashboard rooms allocation hall tickets seating results attendance',
  },
  {
    title: 'Exam Room Management',
    section: 'Examination Management',
    path: '/admin/academic/examinations/room-management',
    keywords: 'exam room management seating capacity room allocation exam rooms',
  },
  {
    title: 'Room Allocation',
    section: 'Examination Management',
    path: '/admin/academic/examinations/room-allocation',
    keywords: 'room allocation auto allocation examination rooms students seating strategy',
  },
  {
    title: 'Seating Plan',
    section: 'Examination Management',
    path: '/admin/academic/examinations/seating-plan',
    keywords: 'seating plan seats room exam desk student arrangement print export',
  },
  {
    title: 'Seating Rules',
    section: 'Examination Management',
    path: '/admin/academic/examinations/seating-rules',
    keywords: 'seating rules configuration seating patterns special arrangements automatic allocation',
  },
  {
    title: 'Hall Ticket',
    section: 'Examination Management',
    path: '/admin/academic/examinations/hall-ticket',
    keywords: 'hall ticket admit card preview download publish student exam hall',
  },
  {
    title: 'Fee Structure',
    section: 'Finance Management',
    path: '/admin/finance/fees-finance/fee-structure',
    keywords: 'fee structure setup class fee type due date frequency amount',
  },
  {
    title: 'Payments',
    section: 'Finance Management',
    path: '/admin/finance/fees-finance/payments',
    keywords: 'payments transactions collect payment invoice paid pending due partial cash cheque online bank transfer',
  },
  {
    title: 'Discounts',
    section: 'Finance Management',
    path: '/admin/finance/fees-finance/discounts',
    keywords: 'discounts scholarships concessions sibling merit staff ward',
  },
  {
    title: 'Finance Reports',
    section: 'Finance Management',
    path: '/admin/finance/fees-finance/reports',
    keywords: 'reports financial insights export collections',
  },
  {
    title: 'Inventory Management',
    section: 'Inventory Management',
    path: '/admin/inventory-management',
    keywords: 'inventory physical book copies library barcode rfid catalog total available issued repair lost',
  },
  {
    title: 'Alumni Overview',
    section: 'Alumni Management',
    path: '/admin/alumini/overview',
    keywords: 'alumni alumini overview donation engagement graduates',
  },
  {
    title: 'Events',
    section: 'Communication',
    path: '/admin/events',
    keywords: 'events clubs event dashboard annual day sports championship science expo communication',
  },
  {
    title: 'Communication Dashboard',
    section: 'Communication',
    path: '/admin/communication',
    keywords: 'communication notices messages polls surveys announcements groups engagement alerts',
  },
  {
    title: 'Messages',
    section: 'Communication',
    path: '/admin/communication/message',
    keywords: 'compose message broadcast recipients students parents staff school wide',
  },
  {
    title: 'Compose New Message',
    section: 'Communication',
    path: '/admin/communication/compose',
    keywords: 'compose new message broadcast recipients students parents staff school wide',
  },
  {
    title: 'Notices',
    section: 'Communication',
    path: '/admin/communication/notices',
    keywords: 'notices announcement notice templates school-wide communication',
  },
  {
    title: 'Notice Details',
    section: 'Communication',
    path: '/admin/communication/notices/notice-id',
    keywords: 'notice details published notice review share archive pin',
  },
  {
    title: 'Create Notice',
    section: 'Communication',
    path: '/admin/communication/notices/create',
    keywords: 'create notice compose publish announcement school communication',
  },
  {
    title: 'Groups',
    section: 'Communication',
    path: '/admin/communication/groups',
    keywords: 'groups recipient lists audience transport route clubs communication',
  },
  {
    title: 'Academics',
    section: 'Academic Management',
    path: '/admin/academics',
    keywords: 'academics academic year grades sections curriculum semesters terms institutional calendar',
  },
  {
    title: 'Subject Management',
    section: 'Academic Management',
    path: '/admin/academics/subject-management',
    keywords: 'subject management subjects active core elective category grade level academic',
  },
  {
    title: 'Timetable Management',
    section: 'Academic Management',
    path: '/admin/academics/timetable',
    keywords: 'timetable schedule periods classes teachers academic management',
  },
  {
    title: 'Classes',
    section: 'Class Management',
    path: '/admin/class-management/classes',
    keywords: 'class management assign students sections occupancy transfer promote classes',
  },
  {
    title: 'Pending Queue',
    section: 'Class Management',
    path: '/admin/class-management/pending-queue',
    keywords: 'pending queue pending class assignment admissions allocation urgent students seats grades',
  },
  {
    title: 'Route & Stop Management',
    section: 'Operations Management',
    path: '/admin/transport/routes',
    keywords: 'routes management pickup drop route planning transport route create edit delete stops',
  },
  {
    title: 'Vehicle Management',
    section: 'Operations Management',
    path: '/admin/transport/vehicles',
    keywords: 'vehicles buses fleet transport operations fleet management',
  },
  {
    title: 'Driver & Attendant Management',
    section: 'Operations Management',
    path: '/admin/transport/driver-attendants',
    keywords: 'drivers attendant transport operation staff duty conductor crew',
  },
  {
    title: 'Student Transport Allocation',
    section: 'Operations Management',
    path: '/admin/transport/student-allocation',
    keywords: 'student allocation transport allocation bus allocation transport seats route assignment',
  },
  {
    title: 'Transport Fee',
    section: 'Operations Management',
    path: '/admin/transport/fee-bands',
    keywords: 'transport fee fee bands distance slabs fee configuration billing',
  },
  {
    title: 'Transport Reports & Alerts',
    section: 'Operations Management',
    path: '/admin/transport/reports',
    keywords: 'transport reports alerts compliance audit fleet reports student allocation report',
  },
  {
    title: 'Alumni Directory',
    section: 'Alumni Management',
    path: '/admin/alumini/directory',
    keywords: 'alumni alumini directory members graduates profiles verification',
  },
  {
    title: 'Verification Requests',
    section: 'Alumni Management',
    path: '/admin/alumini/directory/verification-requests',
    keywords: 'verification requests alumni approval pending profiles',
  },
  {
    title: 'Programs & Events',
    section: 'Alumni Management',
    path: '/admin/alumini/programs-events',
    keywords: 'programs events news alumni calendar create news',
  },
  {
    title: 'Alumni Community',
    section: 'Alumni Management',
    path: '/admin/alumini/community',
    keywords: 'community moderation posts alumni comments reports',
  },
  {
    title: 'Contributions',
    section: 'Alumni Management',
    path: '/admin/alumini/contributions',
    keywords: 'contributions donation campaigns alumni fundraising money',
  },
  {
    title: 'Job Posting & Career Moderation',
    section: 'Alumni Management',
    path: '/admin/alumini/job-posting-career-moderation',
    keywords: 'jobs career moderation postings alumni opportunities',
  },
]

function getAdminPageLabel(pathname: string) {
  if (pathname === '/admin/student-directory' || pathname.startsWith('/admin/frontoffice')) {
    return 'Student Directory'
  }

  if (pathname === '/admin/enrollment/pending') {
    return 'Pending Enrollment'
  }

  if (pathname === '/admin/student-lifecycle') {
    return 'Student Lifecycle'
  }

  if (pathname === '/admin/enrollment/class-section-setup' || pathname === '/admin/enrollment/seat-management') {
    return 'Class & Section Setup'
  }

  if (pathname === '/admin/enrollment/class-section-setup/add-class' || pathname === '/admin/enrollment/seat-management/add-class') {
    return 'Add Class'
  }

  if (pathname === '/admin/enrollment/class-section-setup/edit-class') {
    return 'Edit Class'
  }

  if (pathname === '/admin/enrollment/class-section-setup/view-class') {
    return 'View Class Details'
  }

  if (pathname === '/admin/enrollment/class-section-setup/add-section' || pathname === '/admin/enrollment/seat-management/add-section') {
    return 'Add Section'
  }

  if (pathname === '/admin/enrollment/class-section-setup/set-criteria' || pathname === '/admin/enrollment/seat-management/set-criteria') {
    return 'Set criteria'
  }

  if (pathname === '/admin/dashboard') {
    return 'Dashboard'
  }

  if (pathname === '/admin/user-management') {
    return 'User Management'
  }

  if (pathname === '/admin/user-management/departments') {
    return 'Department Management'
  }

  if (pathname === '/admin/user-management/new') {
    return 'Add New User'
  }

  if (pathname === '/admin/academic/examinations') {
    return 'Examination Management'
  }

  if (pathname === '/admin/academic/examinations/timetable') {
    return 'Examination Timetable'
  }

  if (pathname === '/admin/academic/examinations/room-management') {
    return 'Exam Room Management'
  }

  if (pathname === '/admin/academic/examinations/room-allocation') {
    return 'Room Allocation'
  }

  if (pathname === '/admin/academic/examinations/seating-plan') {
    return 'Seating Plan'
  }

  if (pathname === '/admin/academic/examinations/seating-rules') {
    return 'Seating Rules'
  }

  if (pathname === '/admin/academic/examinations/hall-ticket') {
    return 'Hall Ticket Generation'
  }

  if (pathname.startsWith('/admin/document-verification')) {
    return 'Document Verification'
  }

  if (pathname === '/admin/events') {
    return 'Events'
  }

  if (pathname === '/admin/communication') {
    return 'Main Dashboard'
  }

  if (pathname === '/admin/communication/compose') {
    return 'Main Dashboard'
  }

  if (pathname === '/admin/communication/message') {
    return 'Messages'
  }

  if (pathname.startsWith('/admin/communication/message/')) {
    return 'Message Details'
  }

  if (pathname === '/admin/communication/notices') {
    return 'Notices'
  }

  if (pathname === '/admin/communication/notices/create') {
    return 'Create Notice'
  }

  if (pathname.startsWith('/admin/communication/notices/')) {
    return 'Notice Details'
  }

  if (pathname === '/admin/communication/groups') {
    return 'Groups'
  }

  if (pathname === '/admin/academics') {
    return 'Academics'
  }

  if (pathname === '/admin/academics/subject-management') {
    return 'Subject Management'
  }

  if (pathname === '/admin/academics/timetable') {
    return 'Timetable Management'
  }

  if (
    pathname.startsWith('/admin/academics/subject-allocation') ||
    pathname.startsWith('/admin/class-management/subject-allocation')
  ) {
    return 'Subject Allocation'
  }

  if (pathname === '/admin/class-management/pending-queue') {
    return 'Pending Queue'
  }

  if (pathname.startsWith('/admin/class-management')) {
    return 'Class Management'
  }

  if (pathname.startsWith('/admin/admission/')) {
    if (pathname.endsWith('/new')) {
      return 'New Application'
    }

    if (pathname.endsWith('/reports')) {
      return 'Admissions Reports'
    }

    return 'Admission Details'
  }

  if (pathname === '/admin/transport' || pathname === '/admin/transport/dashboard') {
    return 'Transport Dashboard'
  }

  if (pathname.startsWith('/admin/transport/student-allocation')) {
    return 'Student Transport Allocation'
  }

  const exactMatch = adminSearchItems.find((item) => item.path.split('?')[0] === pathname)

  if (exactMatch) {
    return exactMatch.title
  }

  if (pathname.startsWith('/admin/enrollment/')) {
    return 'Enrollment Details'
  }

  if (pathname === '/admin/application/new') {
    return 'New Application'
  }

  if (pathname.startsWith('/admin/application/')) {
    return 'Application Details'
  }

  if (pathname.startsWith('/admin/track-candidates/')) {
    return 'Candidate Remarks'
  }

  return 'Student Dashboard'
}

export function DashboardLayout() {
  useNavigationTracker()
  useFcmPushNotification()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [adminSearch, setAdminSearch] = useState('')
  const [adminSearchOpen, setAdminSearchOpen] = useState(false)
  const [adminStatus, setAdminStatus] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const adminSearchRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!adminSearchRef.current?.contains(event.target as Node)) {
        setAdminSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/academics') || location.pathname.startsWith('/organization-admin') || location.pathname.startsWith('/seat-planning') || location.pathname.startsWith('/library-management') || location.pathname.startsWith('/admin-library')
  const isTeacherRoute = location.pathname.startsWith('/teacher')
  const teacherPageTitle = teacherPageTitles[location.pathname] ?? 'Dashboard'
  const isStudentRoute = location.pathname.startsWith('/student')
  const isHRRoute = location.pathname.startsWith('/hr')
  const isExaminationRoute = location.pathname.startsWith('/admin/academic/examinations')
  const hideAdminSidebar = isAdminRoute && location.pathname.endsWith('/process')
  const hideAdminTopbar = false
  const isTransportVehiclesRoute = location.pathname === '/admin/transport/vehicles'
  const isTransportDriversRoute = location.pathname === '/admin/transport/drivers'
  // Hide breadcrumb on full-screen group chat pages (all role variants) — suppresses
  // the stray "Dashboard / Overview" fallback breadcrumb without affecting other pages.
  const isGroupChatRoute = /\/communication\/groups\/[^/]+\/chat$/.test(location.pathname)

  const adminSearchResults = useMemo(() => {
    const term = adminSearch.trim().toLowerCase()

    if (!term) {
      return adminSearchItems
    }

    return adminSearchItems
      .filter((item) =>
        `${item.title} ${item.section} ${item.keywords}`.toLowerCase().includes(term),
      )
      .slice(0, 8)
  }, [adminSearch])

  const adminPageLabel = getAdminPageLabel(location.pathname)
  const adminBreadcrumbPrefix = useMemo(() => {
    const path = location.pathname
    if (path.startsWith('/admin/student-directory') ||
        path.startsWith('/admin/frontoffice') ||
        path.startsWith('/admin/document-verification') || 
        path.startsWith('/admin/admission') || 
        path.startsWith('/admin/application') ||
        path.startsWith('/admin/enrollment') ||
        path.startsWith('/admin/interview') ||
        path.startsWith('/admin/track-candidates') ||
        path.startsWith('/seat-planning')) {
      return 'Front Office'
    }
    if (path.startsWith('/admin/academic/examinations')) {
      return 'Examinations'
    }
    if (path.startsWith('/admin/academics') || 
        path.startsWith('/admin/class-management')) {
      return 'Academics'
    }
    if (path.startsWith('/admin/finance')) {
      return 'Finance'
    }
    if (path.startsWith('/admin/communication') || path.startsWith('/admin/events')) {
      return 'Communication'
    }
    if (path.startsWith('/admin/alumini')) {
      return 'Alumni'
    }
    if (path.startsWith('/admin/leave-management') || path.startsWith('/admin/hr-management/leave-management')) {
      return 'Leave Management'
    }
    if (path.startsWith('/admin/hr-management')) {
      return 'HR Management'
    }
    if (path.startsWith('/admin/transport') || path.startsWith('/admin/library')) {
      return 'Operations'
    }
    if (path.startsWith('/admin/user-management')) {
      return 'User Management'
    }
    return 'Dashboard'
  }, [location.pathname])

  useEffect(() => {
    if (!isAdminRoute) {
      setAdminSearch('')
      setAdminSearchOpen(false)
    }
  }, [isAdminRoute, location.pathname])


  useEffect(() => {
    if (!adminStatus) {
      return
    }

    const timer = window.setTimeout(() => setAdminStatus(null), 2400)
    return () => window.clearTimeout(timer)
  }, [adminStatus])

  const navigateToAdminSearchResult = (item: AdminSearchItem) => {
    navigate(item.path)
    setAdminSearch('')
    setAdminSearchOpen(false)
  }

  const handleAdminSearchSubmit = () => {
    if (adminSearchResults.length) {
      navigateToAdminSearchResult(adminSearchResults[0])
      return
    }

    setAdminStatus('No matching School Admin page found.')
  }

  const { role } = useAuthStore()

  // Sidebar Selector Logic: Uses ONE PermissionAwareSidebar for all application users
  const renderSidebar = () => {
    if (isAdminRoute && hideAdminSidebar) {
      return null
    }

    return <PermissionAwareSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
  }

  return (
    <div className={`app-theme-shell min-h-screen overflow-x-hidden bg-[#f8fafc] ${isAdminRoute ? 'school-admin-shared-topbar-active' : ''}`}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes dashboard-page-enter {
              from {
                opacity: 0;
                transform: translate3d(0, 18px, 0) scale(0.995);
              }
              to {
                opacity: 1;
                transform: translate3d(0, 0, 0) scale(1);
              }
            }

            .page-transition {
              animation: dashboard-page-enter 320ms ease-out both;
              transform-origin: top center;
              will-change: opacity, transform;
            }

            .school-admin-shared-topbar-active .school-admin-content-shell .page-transition header.sticky {
              display: none !important;
            }

            /* Shared topbar global styling overrides matching Figma design tokens */
            .school-admin-topbar {
              font-family: 'Inter', sans-serif;
              border-color: #e9ecef;
            }

            @media (min-width: 1024px) {
              .school-admin-topbar {
                height: 70px !important;
              }
              .school-admin-topbar > div:first-child {
                height: 100% !important;
              }
            }

            .school-admin-topbar-breadcrumb {
              font-family: 'Inter', sans-serif !important;
              font-size: 14px !important;
              font-weight: 500 !important;
              line-height: 20px !important;
              letter-spacing: -0.01em !important;
              color: #3f4448 !important;
            }

            .school-admin-topbar-breadcrumb span {
              font-weight: 600 !important;
              color: #111111 !important;
            }

            .school-admin-topbar h1 {
              font-family: 'Inter', sans-serif !important;
              font-size: 28px !important;
              font-weight: 600 !important;
              letter-spacing: -0.03em !important;
              line-height: 34px !important;
              color: #1c3558 !important;
            }

            .school-admin-topbar p:not(.school-admin-user-role):not(.school-admin-user-name) {
              font-family: 'Inter', sans-serif !important;
              font-size: 14px !important;
              font-weight: 500 !important;
              color: #b1bfd0 !important;
            }

            .school-admin-global-search {
              font-family: 'Inter', sans-serif !important;
              font-size: 15px !important;
              color: #111111 !important;
              border-radius: 7px !important;
            }

            .school-admin-user-role {
              font-family: 'Inter', sans-serif !important;
              font-size: 14px !important;
              font-weight: 700 !important;
              color: #191C1D !important;
            }

            .school-admin-user-name {
              font-family: 'Inter', sans-serif !important;
              font-size: 12px !important;
              font-weight: 500 !important;
              color: #3F484A !important;
            }

            .school-admin-topbar-button {
              border-radius: 9999px !important;
            }
          `,
        }}
      />

      {/* Dynamic Sidebar Rendering */}
      {renderSidebar()}

      <div
        className={`${(isAdminRoute && hideAdminSidebar) ? 'lg:pl-0' : 'lg:pl-[240px]'} flex min-h-screen flex-col ${isAdminRoute ? 'school-admin-content-shell' : ''}`}
      >
        {!hideAdminTopbar && (
          <Header
            onMenuClick={() => setSidebarOpen(true)}
            hideSidebar={Boolean(isAdminRoute && hideAdminSidebar)}
            breadcrumb={
              isAdminRoute || location.pathname.startsWith('/communication')
                ? getAdminBreadcrumbParts(location.pathname)
                : isTeacherRoute
                ? getTeacherBreadcrumbParts(location.pathname)
                : undefined
            }
          />
        )}

        {/* Mobile Overlay */}
        {sidebarOpen && !hideAdminSidebar && (
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}


        <main
          className={`flex-1 w-full ${
            isStudentRoute
              ? 'student-module px-4 pb-6 pt-[88px] lg:px-5 lg:pb-8 lg:pt-[92px]'
              : isTeacherRoute
                ? 'px-4 pb-6 pt-[76px] lg:px-6 lg:pb-8 lg:pt-[80px]'
                : isAdminRoute
                  ? `w-full px-4 lg:px-6 ${isTransportVehiclesRoute || isTransportDriversRoute ? 'pt-[130px]' : 'pt-[100px]'} lg:pt-[90px]`
                  : isHRRoute
                    ? 'hr-module w-full p-0'
                    : 'w-full px-4 pb-6 pt-[84px] lg:px-8 lg:pb-8 lg:pt-[92px]'
          }`}
        >
          <div key={location.pathname} className="page-transition min-h-full w-full">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet context={{ setSidebarOpen, adminSearch, setAdminSearch }} />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  )
}
